import * as React from 'react'
import { AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Label } from './label'

interface FieldProps {
  /** The field's label text. */
  label: React.ReactNode
  /** Associates the label with a control by its `id`. Omit for controls that
   * label themselves (e.g. a `TimePicker` trigger uses `ariaLabel`). */
  htmlFor?: string
  /** Optional helper text shown under the control (hidden when `error` is set). */
  hint?: React.ReactNode
  /** Optional error message shown under the control, in the destructive color. */
  error?: React.ReactNode
  /** `sm` renders the compact label used in dense rows (smaller, muted). */
  size?: 'default' | 'sm'
  /** Extra classes for the field wrapper (e.g. grid-cell width, `flex-1`). */
  className?: string
  /** The control(s): an `Input`, `Select`, `TimePicker`, editor, etc. */
  children: React.ReactNode
}

/** Aria props the field pushes onto its control. */
interface DescribedControlProps {
  // `| undefined` spelled out for exactOptionalPropertyTypes: cloneElement is
  // passed these unconditionally, and "absent" has to be expressible.
  'aria-invalid'?: boolean | undefined
  'aria-describedby'?: string | undefined
}

/**
 * A prop's value when it is a string or a boolean, otherwise undefined.
 *
 * Element props are not a type we can name — a child can be any element — so
 * the two aria props are read by checking them, not by asserting the element.
 */
function propValue(props: object, key: string): string | boolean | undefined {
  if (!Object.hasOwn(props, key)) return undefined
  for (const [name, value] of Object.entries(props)) {
    if (name !== key) continue
    if (typeof value === 'string' || typeof value === 'boolean') return value
    return undefined
  }
  return undefined
}

/**
 * A single element whose aria props, when present, are the types we forward.
 *
 * `isValidElement`'s type argument would claim the props without looking.
 * Checking the two props we read is what makes the narrowing true.
 */
function isDescribedElement(node: React.ReactNode): node is React.ReactElement<DescribedControlProps> {
  if (!React.isValidElement(node)) return false
  const props = node.props
  if (typeof props !== 'object' || props === null) return false
  if (Object.hasOwn(props, 'aria-invalid') && typeof propValue(props, 'aria-invalid') !== 'boolean') return false
  if (Object.hasOwn(props, 'aria-describedby') && typeof propValue(props, 'aria-describedby') !== 'string') {
    return false
  }
  return true
}

/**
 * A single form field: label stacked above its control, with optional hint and
 * error text. Owning the layout here keeps label placement and spacing identical
 * across every form — pages compose fields instead of hand-assembling a wrapper,
 * a `<Label>`, and the control, which is how they used to drift.
 *
 * The hint or error is also announced: the message gets an id and the control is
 * given `aria-describedby` pointing at it, plus `aria-invalid` when there is an
 * error. Without that the red text is purely visual — a screen reader reaches the
 * input with no indication that it is invalid or why, which is the state every
 * form in the app was in before.
 *
 * Wiring happens by cloning a single element child. A field holding several
 * controls (or a fragment) still renders and labels correctly; it just cannot
 * know which control the message belongs to, so it leaves the aria props alone.
 */
export function Field({ label, htmlFor, hint, error, size = 'default', className, children }: FieldProps) {
  const generatedId = React.useId()
  const messageId = `${htmlFor ?? generatedId}-message`
  const message = error ?? hint
  const describedBy = message == null ? undefined : messageId

  const control = isDescribedElement(children)
    ? React.cloneElement(children, {
        'aria-invalid': error != null ? true : children.props['aria-invalid'],
        // Keep anything the caller already pointed at; ours is additive.
        'aria-describedby':
          [children.props['aria-describedby'], describedBy].filter((part) => part !== undefined && part !== '').join(' ') ||
          undefined,
      })
    : children

  return (
    <div className={cn('grid min-w-0 content-start', size === 'sm' ? 'gap-1' : 'gap-1.5', className)}>
      <Label htmlFor={htmlFor} className={size === 'sm' ? 'text-xs text-ink-muted' : undefined}>
        {label}
      </Label>
      {control}
      {error ? (
        <p id={messageId} className="flex items-start gap-1.5 text-small text-danger-ink">
          <AlertCircle aria-hidden className="mt-px h-3.5 w-3.5 shrink-0" />
          <span>{error}</span>
        </p>
      ) : hint ? (
        <p id={messageId} className="text-small text-ink-muted">
          {hint}
        </p>
      ) : null}
    </div>
  )
}
